/**
 * The stage gate, turned from an obstacle into a prompt.
 *
 * §6.1 refuses a move whose `required_fields` are unpopulated with
 * `422 STAGE_GATE_FAILED`, carrying the missing fields and the stage's
 * `entry_criteria`. Phase 4: "do not silently fail: open a compact dialog
 * showing the stage's entry_criteria sentence above the list of missing
 * fields, let the user fill them inline, and complete the move on submit."
 *
 * Two kinds of missing field arrive, and they need different treatment:
 *
 *  - **columns of `opportunities`** — filled in here, saved with one `PUT`,
 *    and the move retried in the same click;
 *  - **the `@`-sentinels** (`@contact`, `@product`, `@product_line`,
 *    `@design_engineer_contact`) — these are rows in child tables, not fields.
 *    A one-line input cannot create a contact with a buying role or a product
 *    line with units and an ASP, so the dialog says what is missing in the
 *    words the sentinel means and links to the tab that adds it. Pretending
 *    otherwise would produce a form that cannot satisfy its own gate.
 *
 * **`program_id` is the one column that could not be supplied at all**, which
 * is what made `QUALIFIED` unreachable by hand: the picker below listed the
 * account's programs and nothing in the application could create one. It now
 * has a `New program` button beside it, because this is where the user is told
 * they need a program — next to the empty picker — rather than two screens
 * away on the account. A program created here is pinned into the picker's
 * options and selected immediately, so the refetch is a correction rather than
 * a wait: `Combobox` taught this codebase that a control holding an id the
 * list has never heard of renders as though nothing happened.
 *
 * The button is **not** gated. `program.create` is `ALL` tiers and the write
 * is scoped to the account by `ANCESTRY` — an answer this dialog does not
 * have, since it is handed an `accountId` and not the account's `canWrite`.
 * Gating on the opportunity's write answer would be the wrong question asked
 * confidently, so the server answers it (§2 rule 1) and a refusal arrives as a
 * message.
 */

import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/label';
import { api, ApiError } from '@/lib/api';
import { useApiQuery, type Paged, type Row } from '@/lib/queries';
import { useToast } from '@/lib/toast';
import { CreateProgramDialog, type CreatedProgram } from '@/features/programs/CreateProgramDialog';

export interface StageGateFailure {
  stage_code: string;
  missingFields: string[];
  entry_criteria: string | null;
}

/** What each sentinel actually asks for, and where it is satisfied. */
const SENTINELS: Record<string, { title: string; detail: string; tab: string }> = {
  '@contact': {
    title: 'A contact on the opportunity',
    detail: 'Any person linked to this deal — the buying committee on the Overview tab.',
    tab: 'overview',
  },
  '@product': {
    title: 'A product line',
    detail: 'Any part number attached to this deal.',
    tab: 'overview',
  },
  '@product_line': {
    title: 'A costed product line',
    detail:
      'A line that is not design support, carrying both annual units and an ASP — the numbers the value rolls up from.',
    tab: 'overview',
  },
  '@design_engineer_contact': {
    title: 'A design engineer',
    detail:
      'A contact whose effective buying role is design engineer. The role can be overridden on this opportunity without changing the contact record.',
    tab: 'overview',
  },
};

const COLUMN_LABELS: Record<string, string> = {
  program_id: 'Customer program',
  expected_decision_date: 'Expected decision date',
  expected_ramp_date: 'Expected ramp date (production volume)',
  expected_first_revenue_date: 'Expected first revenue date (prototype PO)',
  program_years: 'Program years',
  next_step: 'Next step',
};

export function StageGateDialog({
  opportunityId,
  accountId,
  failure,
  onClose,
  onFilled,
}: {
  opportunityId: string;
  accountId: string | null;
  failure: StageGateFailure;
  onClose: () => void;
  /** Called after the fields were saved, so the caller can retry the move. */
  onFilled: () => void;
}): React.ReactElement {
  const { push } = useToast();
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [creatingProgram, setCreatingProgram] = useState(false);
  /** Programs created from this dialog, so the picker can offer one at once. */
  const [createdPrograms, setCreatedPrograms] = useState<CreatedProgram[]>([]);
  const programFieldId = useId();

  const programs = useApiQuery<Paged<Row>>(
    ['programs', accountId ?? ''],
    accountId === null ? null : `/api/programs?accountId=${accountId}&pageSize=100`,
  );

  const fetchedPrograms = (programs.data?.items ?? []).map((program) => ({
    id: String(program['id']),
    name: String(program['name']),
  }));
  // Pinned first and deduplicated against the fetched list, so a program drops
  // back to being an ordinary option the moment the refetch returns it.
  const programOptions = [
    ...createdPrograms.filter(
      (created) => !fetchedPrograms.some((program) => program.id === created.id),
    ),
    ...fetchedPrograms,
  ];

  const sentinels = failure.missingFields.filter((field) => field.startsWith('@'));
  const columns = failure.missingFields.filter((field) => !field.startsWith('@'));

  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      const body: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(values)) {
        if (value === '') continue;
        body[key] = key === 'program_years' ? Number(value) : value;
      }
      if (Object.keys(body).length > 0) {
        await api(`/api/opportunities/${opportunityId}`, { method: 'PUT', body });
      }
      onFilled();
    } catch (error) {
      push({
        tone: 'error',
        title: error instanceof ApiError ? error.message : 'The fields could not be saved.',
      });
    } finally {
      setBusy(false);
    }
  };

  const canSubmit =
    sentinels.length === 0 && columns.every((column) => (values[column] ?? '') !== '');

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${failure.stage_code.replace(/_/g, ' ')} needs a little more`}
      description={failure.entry_criteria ?? undefined}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void save()} disabled={busy || !canSubmit}>
            Save and move
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {columns.map((column) =>
          column === 'program_id' ? (
            // The `htmlFor` form, not the implicit one: in the implicit form
            // the button would sit inside the `<label>` and every click on it
            // would also land on the select.
            <Field
              key={column}
              label={COLUMN_LABELS[column] ?? column}
              htmlFor={programFieldId}
              {...(accountId === null
                ? {}
                : {
                    hint: 'The customer end programme this deal designs into. Create one here if it is not listed.',
                  })}
            >
              <div className="flex items-center gap-2">
                <Select
                  id={programFieldId}
                  className="flex-1"
                  value={values[column] ?? ''}
                  onChange={(event) =>
                    setValues((current) => ({ ...current, [column]: event.target.value }))
                  }
                >
                  <option value="">Select a program…</option>
                  {programOptions.map((program) => (
                    <option key={program.id} value={program.id}>
                      {program.name}
                    </option>
                  ))}
                </Select>
                {accountId !== null && (
                  <Button type="button" variant="outline" onClick={() => setCreatingProgram(true)}>
                    New program
                  </Button>
                )}
              </div>
            </Field>
          ) : (
            <Field key={column} label={COLUMN_LABELS[column] ?? column}>
              <Input
                type={
                  column.endsWith('_date') ? 'date' : column === 'program_years' ? 'number' : 'text'
                }
                value={values[column] ?? ''}
                onChange={(event) =>
                  setValues((current) => ({ ...current, [column]: event.target.value }))
                }
              />
            </Field>
          ),
        )}

        {sentinels.length > 0 && (
          <div className="space-y-2 rounded-md border border-amber-600/40 bg-amber-50 p-3 text-xs text-amber-950">
            <p className="font-medium">
              These are records, not fields — add them on the opportunity, then move it again.
            </p>
            <ul className="space-y-1.5">
              {sentinels.map((sentinel) => {
                const meaning = SENTINELS[sentinel];
                return (
                  <li key={sentinel}>
                    <span className="font-medium">{meaning?.title ?? sentinel}</span>
                    {meaning !== undefined && <> — {meaning.detail}</>}
                  </li>
                );
              })}
            </ul>
            <Link
              to={`/opportunities/${opportunityId}?tab=overview`}
              className="inline-flex items-center gap-1 font-medium underline underline-offset-2"
              onClick={onClose}
            >
              Open the opportunity
              <ArrowRight className="h-3 w-3" aria-hidden="true" />
            </Link>
          </div>
        )}
      </div>

      {creatingProgram && accountId !== null && (
        <CreateProgramDialog
          accountId={accountId}
          onClose={() => setCreatingProgram(false)}
          onCreated={(program) => {
            setCreatingProgram(false);
            setCreatedPrograms((all) => [...all, program]);
            setValues((current) => ({ ...current, program_id: program.id }));
          }}
        />
      )}
    </Dialog>
  );
}
